import { useParams } from 'react-router-dom'
import { buildErrorMessage, documentationRootGuid, isProjectFileInUseError } from '@constants'
import { AxiosError } from 'axios'
import { reject } from 'lodash-es'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles, getProjectFilesTemplates } from '@queries'
import { deleteProjectFile } from '@mutations'
import queryClient from 'src/state/client'

interface Options {
  onDeleteSuccess?: () => void
  /** The project file is still shown by document items (MD-CORE-0021), instead of the error */
  onFileInUse?: () => void
}

const useDeleteDocument = (rootGuid: string, options?: Options) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    async (documentId: string) => {
      return await deleteProjectFile.request(projectId, rootGuid, documentId)
    },
    {
      onSuccess: (data: string[]) => {
        const filesKey = getProjectFiles.getKey(projectId, rootGuid)

        queryClient.setQueryData<FilesResult>(filesKey, prev => {
          if (!prev) return undefined

          const newFiles = reject(prev.files, f => data.includes(f.guid))
          const newSections = reject(prev.sections, f => data.includes(f.guid))

          return {
            ...prev,
            files: newFiles,
            sections: newSections,
          }
        })

        // Deleted project files and pages change what "Template hinzufügen" offers
        queryClient.invalidateQueries(
          getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
        )

        options?.onDeleteSuccess?.()
      },
      onError: (error: AxiosError) => {
        if (options?.onFileInUse && isProjectFileInUseError(error)) {
          options.onFileInUse()
          return
        }
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen'), { variant: 'error' })
      },
    },
  )
}

export default useDeleteDocument
